import {
  recordLayoutKey,
  recordTypeHasCardLayout,
  type RecordTypesWithCardLayouts,
  type CardLayoutConfigurationData,
} from './cardLayouts';
import type WhiteboardController from './controllers/WhiteboardController';
import { hasRecordReference, isShapeRecord } from './objects/guards';
import { type WhiteboardRecordInfo } from './shared-types';
import { normalizeRecordType } from './utils/record';

class CardLayoutManager {
  defaultCardLayout?: Aha.CardLayout | undefined | null;
  ready: Promise<Aha.CardLayout>;
  readyResolver: (value: any) => void;
  readyRejecter: (reason?: any) => void;

  constructor(private readonly controller: WhiteboardController) {
    this.ready = new Promise<Aha.CardLayout>((resolve, reject) => {
      this.readyResolver = resolve;
      this.readyRejecter = reject;
    });
  }

  _cardLayouts = new Map<RecordTypesWithCardLayouts, Aha.CardLayout>();

  async loadDefaultCardLayout() {
    if (this.cardLayoutsEnabled && !this.defaultCardLayout) {
      const { loadDefaultCardLayout } = this.controller.props;

      if (!loadDefaultCardLayout) {
        return;
      }

      try {
        this.defaultCardLayout = await loadDefaultCardLayout();
      } catch (ex) {
        this.readyRejecter('Error loading defaultCardLayout.');
        throw ex;
      }

      if (this.defaultCardLayout) {
        this.readyResolver(this.defaultCardLayout);
      } else if (this.defaultCardLayout === null) {
        this.readyRejecter('No defaultCardLayout found.');
      } else {
        this.readyRejecter('Error loading defaultCardLayout.');
      }
    }
  }

  get cardLayoutsEnabled(): boolean {
    return Boolean(
      !this.controller.isShared &&
        this.controller.props.allowUpdate &&
        typeof this.controller.props.openCardLayoutEditor === 'function' &&
        typeof this.controller.props.loadDefaultCardLayout === 'function'
    );
  }

  get cardLayoutsReady(): boolean {
    return Boolean(this.cardLayoutsEnabled && this.defaultCardLayout);
  }

  buildCardLayout(recordTypes: RecordTypesWithCardLayouts[]) {
    const recordLayoutKeys = recordTypes.map(recordType =>
      recordLayoutKey(recordType)
    );

    const cardLayoutConfiguration =
      this.controller.dataStoreGlobal?.cardLayout || {};
    const currentLayoutAttributes: Partial<Aha.CardLayoutAttributes> = {
      __typename: 'CardLayout',
    };
    recordLayoutKeys.forEach(recordLayoutKey => {
      if (cardLayoutConfiguration[recordLayoutKey]) {
        currentLayoutAttributes[recordLayoutKey] =
          cardLayoutConfiguration[recordLayoutKey];
      }
    });

    // eslint-disable-next-line no-restricted-globals
    const cardLayout = aha.models.CardLayout.fromJSON(currentLayoutAttributes);

    // We only ever call buildCardLayout when we know the defaultCardLayout is defined
    const defaultCardLayout = this.defaultCardLayout as Aha.CardLayout;
    // eslint-disable-next-line no-restricted-globals
    return aha.models.CardLayout.buildActiveCardLayout({
      cardLayout,
      defaultCardLayout,
      cardViews: ['whiteboard'],
      recordTypes,
    });
  }

  getCardLayout(
    recordType: RecordTypesWithCardLayouts
  ): Aha.CardLayout | undefined {
    if (!this.cardLayoutsReady) {
      return;
    }

    if (!this._cardLayouts.has(recordType)) {
      this._cardLayouts.set(recordType, this.buildCardLayout([recordType]));
    }

    return this._cardLayouts.get(recordType);
  }

  getCardView(
    recordType: RecordTypesWithCardLayouts
  ): Aha.CardView | undefined {
    if (process.env.TEST) {
      return;
    }

    const cardLayout = this.getCardLayout(recordType);

    if (!cardLayout) return;

    return cardLayout.getCardView(recordType, 'whiteboard');
  }

  cardViewForRecord(record: WhiteboardRecordInfo): Aha.CardView | undefined {
    const { typename } = record;

    if (recordTypeHasCardLayout(typename)) {
      return this.getCardView(typename);
    }
  }

  async reloadRecordsAndLayouts() {
    // Kill cached card layout, reload all records
    this._cardLayouts = new Map();

    const { canvas } = this.controller;

    const recordObjects = canvas
      .getObjects()
      .filter(isShapeRecord)
      .filter(hasRecordReference);

    await Promise.all(
      recordObjects.map(
        async object => await object.shape.prepareLinkedRecord()
      )
    );
  }

  openCardLayoutEditor(selection: fabric.Object) {
    const { openCardLayoutEditor } = this.controller.props;

    if (!this.cardLayoutsEnabled || !openCardLayoutEditor) {
      return;
    }

    const recordTypes = selection
      .getObjects()
      .filter(hasRecordReference)
      .filter(isShapeRecord)
      .map(shape => normalizeRecordType(shape.recordType))
      .filter(Boolean)
      .filter(recordTypeHasCardLayout);

    const cardLayout = this.buildCardLayout(recordTypes);

    void openCardLayoutEditor({
      callback: async cardLayout => {
        // Only update the relevant record layouts
        const recordLayouts: CardLayoutConfigurationData = {};
        recordTypes.forEach(recordType => {
          const key = recordLayoutKey(recordType);
          const recordLayout = cardLayout[key];

          if (recordLayout) {
            // Remove the layout if it's inherited (default)
            recordLayouts[key] = recordLayout.whiteboard?.inherited
              ? null
              : recordLayout.toJSON();
          }
        });

        this.controller.dataStoreGlobal?.cardLayout.saveCardLayout(
          recordLayouts
        );

        await this.reloadRecordsAndLayouts();
        this.controller.undoManager.completeUndoGroup();
      },
      cardLayout,
      visibleRecordTypes: [...new Set(recordTypes)],
    });
  }
}

export default CardLayoutManager;
